/**
 * Root App component
 * Composes AppProviders (context stack) with AppRouter (route definitions).
 * All route definitions live in router/Router.tsx - this file only handles providers.
 */

import React from 'react'

import { useAuth } from '@/platform/auth/hooks/useAuth'
import { useDarkMode, useApplyServerThemePreference } from '@/shared/theme'

import { AppProviders } from './AppProviders'
import { AppRouter } from './router/Router'

/**
 * Inner component that can access theme + auth context from AppProviders.
 */
const AppContent: React.FC = () => {
  const { isDark } = useDarkMode()
  const { user } = useAuth()
  // Apply the user's server-persisted theme on login (fresh device / first
  // login) so the saved preference is honored on load. A device-local override
  // still wins — see useApplyServerThemePreference for the precedence rules.
  useApplyServerThemePreference(user?.preferences?.theme)
  return <AppRouter isDark={isDark} />
}

const App: React.FC = () => (
  <AppProviders>
    <AppContent />
  </AppProviders>
)

export default App
